import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CTable from "../../../components/Table/table";
import Select from "react-select";
import { brandPalette } from "../../../constants/brandPalette";
import { adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import {
  homeworkUncommentReportRequest,
} from "../../../modules/admin-modules/reports/Actions";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { CSVLink } from "react-csv";
import { getAllHomeworksRequest } from "../../../modules/admin-modules/homework/Actions";
import { RiDeleteBinLine, RiSearchLine } from "react-icons/ri";
import HomeworkComment from "../../../containers/admin/reports/homework-comment";
import HomeworkRefuseComment from "../../../containers/admin/reports/refuse-homework";
import { openAdminHomeworkCommentModel, openRefuseHomeworkModel } from "../../../modules/admin-modules/models/Actions";

const homeworkSelectBase = adminSelectStyles({ menuZIndex: 50 });
const homeworkSelectStyles = {
  ...homeworkSelectBase,
  control: (styles, state) => ({
    ...homeworkSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
  }),
};

const LATE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

function getSubmittedAt(row) {
  return row?.submitted_at ?? row?.created_at;
}

function isSubmissionLate(row) {
  const submittedAt = getSubmittedAt(row);
  if (!submittedAt) return false;
  return Date.now() - new Date(submittedAt).getTime() >= LATE_THRESHOLD_MS;
}

function formatSubmittedAt(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  return date
    .toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
    .replace(",", "");
}

function LateBadge() {
  return (
    <Badge
      px={2}
      py={0.5}
      borderRadius="md"
      fontWeight="700"
      fontSize="xs"
      bg="rgba(155, 44, 44, 0.12)"
      color="#9B2C2C"
    >
      Late
    </Badge>
  );
}

const HomeworkUncommentReport = () => {
  const dispatch = useDispatch();
  const [homeworkId, setHomeworkId] = useState("");
  const [codeSearch, setCodeSearch] = useState("");
  const [lateOnly, setLateOnly] = useState(false);

  const [offset, setPage] = useState(0);
  const [size, setPerPage] = useState(10);
  const [pageNumber, setPageNumber] = useState(0);
  const [data, setData] = useState(null);
  useEffect(() => {
    if (homeworkId) dispatch(homeworkUncommentReportRequest(homeworkId));
  }, [homeworkId, dispatch]);

  useEffect(() => {
    setCodeSearch("");
    setLateOnly(false);
    setPageNumber(0);
    setPage(0);
  }, [homeworkId]);

  useEffect(() => {
    setPageNumber(0);
    setPage(0);
  }, [codeSearch, lateOnly]);

  useEffect(() => {
    dispatch(getAllHomeworksRequest());
  }, [dispatch]);

  const homeworks = useSelector((state) => state.homework.homeworks);
  const homeworkUncomment = useSelector(
    (state) => state.reports.homeworkUncomment
  );
  const isLoading = useSelector((state) => state.reports.isLoading);

  const homeworksOptions = homeworks?.map((data) => {
    return {
      value: data.id,
      label: `${data.name} (${data.unevaluated_count || 0})`,
    };
  });

  const filteredSubmissions = useMemo(() => {
    if (!Array.isArray(homeworkUncomment)) return [];
    const query = codeSearch.trim().toLowerCase();
    return homeworkUncomment
      .filter((row) => {
        const userName = String(row?.student?.user_name ?? "").toLowerCase();
        if (query && !userName.includes(query)) return false;
        if (lateOnly && !isSubmissionLate(row)) return false;
        return true;
      })
      .sort((a, b) => new Date(getSubmittedAt(a)) - new Date(getSubmittedAt(b)));
  }, [homeworkUncomment, codeSearch, lateOnly]);

  const lateCount = useMemo(
    () =>
      Array.isArray(homeworkUncomment)
        ? homeworkUncomment.filter((row) => isSubmissionLate(row)).length
        : 0,
    [homeworkUncomment]
  );

  const viewData = (data) => {
    setData(data);
  };
  const headers = [
    { label: "name", key: "name" },
    { label: "user_name", key: "user_name" },
    { label: "group", key: "group" },
    { label: "created_at", key: "created_at" },
    { label: "status", key: "status" },
  ];
  const Data = filteredSubmissions?.map((data, index) => {
    const late = isSubmissionLate(data);
    return {
      ...data,
      index: `#${index + 1}`,
      id: data?.id,
      name: data?.student?.name,
      user_name: data?.student?.user_name,
      group: data?.student_group,
      created_at: formatSubmittedAt(getSubmittedAt(data)),
      status: late ? "Late" : "",
      status_badge: late ? <LateBadge /> : null,
      preview_answer: (
        <Button
          onClick={() => dispatch(openAdminHomeworkCommentModel())}
          size="sm"
          borderRadius="12px"
          bg={brandPalette.secondary}
          color="white"
          _hover={{ bg: brandPalette.primary }}
        >
          Review submission
        </Button>
      ),
      delete: (
        <IconButton
          aria-label="Reject submission"
          size="sm"
          borderRadius="12px"
          variant="ghost"
          icon={<RiDeleteBinLine color="red" />}
          onClick={() =>
            dispatch(
              openRefuseHomeworkModel()
            )
          }
        />
      ),
    };
  });
  const columns = [
    {
      Header: "#",
      accessor: "index",
    },
    {
      Header: "Student name",
      accessor: "name",
    },
    {
      Header: "Code",
      accessor: "user_name",
    },
    {
      Header: "Group",
      accessor: "group",
    },
    {
      Header: "Submitted at",
      accessor: "created_at",
    },
    {
      Header: "Status",
      accessor: "status_badge",
    },
    {
      Header: " ",
      accessor: "preview_answer",
    },

    {
      Header: "  ",
      accessor: "delete",
    },
  ];

  const totalPage = Math.max(1, Math.ceil((Array.isArray(Data) ? Data.length : 0) / (size || 10)));

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={5}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "center" }}
          gap={3}
          flexWrap="wrap"
        >
          <Box>
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Homework submissions (Pending review)
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Pick a homework to review submissions. Late = unchecked for 24h+ since submit.
            </Text>
          </Box>

          <Flex align="center" gap={2} flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <Box minW={{ base: "100%", md: "340px" }}>
              <Select
                options={homeworksOptions}
                isMulti={false}
                onChange={(d) => setHomeworkId(d?.value ?? "")}
                styles={homeworkSelectStyles}
                placeholder="Select homework"
              />
            </Box>

            <Box
              border="1px solid"
              borderColor="var(--border-subtle)"
              bg="var(--surface-muted)"
              borderRadius="14px"
              p="6px"
            >
              <CSVLink
                headers={headers}
                data={Array.isArray(Data) && Data.length !== 0 ? Data : []}
                filename={"homework-uncomment-report.csv"}
                target="_blank"
                style={{ textDecoration: "none" }}
              >
                <Button
                  size="sm"
                  borderRadius="12px"
                  bg="var(--surface-raised)"
                  border="1px solid"
                  borderColor="var(--border-subtle)"
                  _hover={{ bg: "var(--surface-muted)" }}
                  fontWeight="800"
                  color="var(--text-primary)"
                >
                  Download CSV
                </Button>
              </CSVLink>
            </Box>

            <LogoutMenu />
          </Flex>
        </Flex>

        <Box
          border="1px solid"
          borderColor="var(--border-subtle)"
          bg="var(--surface-raised)"
          borderRadius="18px"
          p={{ base: 4, md: 6 }}
          boxShadow="0 18px 55px rgba(0,0,0,0.12)"
        >
          <Flex
            mb={5}
            pb={5}
            gap={3}
            flexWrap="wrap"
            align={{ base: "stretch", md: "center" }}
            borderBottom="1px solid"
            borderColor="var(--border-subtle)"
          >
            <InputGroup flex={{ base: "1 1 100%", md: "1 1 240px" }} maxW={{ md: "320px" }}>
              <InputLeftElement pointerEvents="none" h="44px">
                <RiSearchLine color="var(--text-muted)" />
              </InputLeftElement>
              <Input
                value={codeSearch}
                onChange={(e) => setCodeSearch(e.target.value)}
                placeholder={
                  homeworkId ? "Search by student code" : "Select homework first"
                }
                isDisabled={!homeworkId}
                h="44px"
                pl="2.5rem"
                borderRadius="14px"
                bg="var(--surface-muted)"
                borderColor="var(--border-subtle)"
                color="var(--text-primary)"
                _placeholder={{ color: "var(--text-muted)" }}
                _focus={{
                  borderColor: brandPalette.primary,
                  boxShadow: "0 0 0 3px rgba(91, 13, 22, 0.12)",
                }}
                _disabled={{ opacity: 0.6, cursor: "not-allowed" }}
              />
            </InputGroup>

            <Flex
              align="center"
              gap={3}
              px={4}
              h="44px"
              borderRadius="14px"
              border="1px solid"
              borderColor="var(--border-subtle)"
              bg="var(--surface-muted)"
              opacity={homeworkId ? 1 : 0.6}
            >
              <Checkbox
                isChecked={lateOnly}
                onChange={(e) => setLateOnly(e.target.checked)}
                isDisabled={!homeworkId}
                colorScheme="red"
                size="md"
              >
                <Text fontSize="sm" fontWeight="700" color="var(--text-primary)">
                  Late only (24h+)
                </Text>
              </Checkbox>
              {homeworkId && lateCount > 0 ? (
                <Badge
                  px={2}
                  py={0.5}
                  borderRadius="md"
                  fontWeight="700"
                  fontSize="xs"
                  bg="rgba(155, 44, 44, 0.12)"
                  color="#9B2C2C"
                >
                  {lateCount} late
                </Badge>
              ) : null}
            </Flex>

            {homeworkId ? (
              <Text fontSize="sm" fontWeight="600" color="var(--text-muted)" alignSelf="center">
                {filteredSubmissions.length} of {homeworkUncomment?.length ?? 0} shown
              </Text>
            ) : (
              <Text fontSize="sm" fontWeight="600" color="var(--text-muted)" alignSelf="center">
                Select a homework above to load submissions
              </Text>
            )}
          </Flex>

          <CTable
            selectedData={viewData}
            Columns={columns}
            Data={Data}
            path={"openModel"}
            Actions
            Title={``}
            subTitle=""
            btnTitle=""
            placeHolder=""
            noSearchBar={true}
            noFilter={true}
            footerBtnTitle
            filterList
            addButton
            addButtonTitle=""
            setPage={setPage}
            setPerPage={setPerPage}
            currentpage={pageNumber}
            setPageNumber={setPageNumber}
            noSecondSearchBar={true}
            isLoading={isLoading}
            perPage={size}
            totalPage={totalPage}
          />
        </Box>
      </Box>
      <HomeworkComment data={data} homeworkId={homeworkId} />
      <HomeworkRefuseComment data={data} homeworkId={homeworkId} />
    </Layout>
  );
};

export default HomeworkUncommentReport;
